/**
 * chainLeak — single source of truth for the Learning Impact Chain
 * stages and the "live value leak" detection shared across the home
 * dashboard chain, the per-page ChainPositionStrip, and the guided
 * tour.
 *
 * The leak is the chain stage with the steepest quarter-over-quarter
 * %-drop (from `/health/quarter-deltas`). %-change normalises across
 * metrics with different scales (ROI is stored ×100). Only stages
 * that actually declined qualify as a leak.
 */

// Canonical chain order. `metric` maps to the key returned by
// /health/quarter-deltas; `path` deep-links to each stage's page
// (Business Outcomes lives on Score Trends' KPI tab since it has no
// standalone Health dashboard); `nodeTestid` matches the home
// dashboard ChainNode's data-testid so the tour can spotlight it.
export const CHAIN_STAGES = [
  { key: 'learning', label: 'Learning', dashLabel: 'LEARNING', hint: 'LEI', metric: 'lei', path: '/lei-health', nodeTestid: 'chain-node-learning' },
  { key: 'capability', label: 'Capability', dashLabel: 'CAPABILITY', hint: 'PE', metric: 'pe', path: '/people-engine', nodeTestid: 'chain-node-capability' },
  { key: 'behavior', label: 'Behavior', dashLabel: 'BEHAVIOR', hint: 'ROE', metric: 'roe', path: '/roe-health', nodeTestid: 'chain-node-behavior' },
  { key: 'outcomes', label: 'Business Outcomes', dashLabel: 'BUSINESS OUTCOMES', hint: 'KPI', metric: 'kpi', path: '/score-trends?tab=kpi', nodeTestid: 'chain-node-business-outcomes' },
  { key: 'financial', label: 'Financial', dashLabel: 'FINANCIAL IMPACT', hint: 'ROI', metric: 'roi', path: '/roi-health', nodeTestid: 'chain-node-financial-impact' },
];

/**
 * Given the `metrics` map from /health/quarter-deltas, return the
 * stage with the steepest decline, or null when no stage declined
 * (or there's no quarter-delta history yet).
 *
 * @returns {{ key, metric, deltaPct, stage }|null}
 */
export function computeChainLeak(metrics) {
  if (!metrics) return null;
  let worst = null;
  for (const stage of CHAIN_STAGES) {
    const m = metrics[stage.metric];
    const dp = m?.delta_pct;
    if (m?.direction === 'down' && typeof dp === 'number' && dp < 0) {
      if (!worst || dp < worst.deltaPct) {
        worst = { key: stage.key, metric: stage.metric, deltaPct: dp, stage };
      }
    }
  }
  return worst;
}


// ── Maturity attribution ───────────────────────────────────────
// Maps each chain stage to the maturity pillar that most governs
// its REALIZATION, so the home chain can attribute each stage's
// maturity-capped headroom to a specific pillar (and tint it).
// `pillarId` matches /maturity-assessment/financial pillar_scores.
// Colors are intentionally distinct, faint backgrounds so the
// headroom band reads as "capped by <this pillar>".
export const STAGE_MATURITY_PILLAR = {
  learning:   { pillarId: 'pillar_1', short: 'Demand',     tint: 'bg-amber-200',  tick: 'bg-amber-500',  text: 'text-amber-700' },
  capability: { pillarId: 'pillar_2', short: 'Skills',     tint: 'bg-violet-200', tick: 'bg-violet-500', text: 'text-violet-700' },
  behavior:  { pillarId: 'pillar_5', short: 'Adoption',   tint: 'bg-orange-200', tick: 'bg-orange-500', text: 'text-orange-700' },
  outcomes:   { pillarId: 'pillar_4', short: 'Insight',    tint: 'bg-rose-200',   tick: 'bg-rose-500',   text: 'text-rose-700' },
  financial:  { pillarId: 'pillar_3', short: 'Governance', tint: 'bg-sky-200',    tick: 'bg-sky-500',    text: 'text-sky-700' },
};

// Derive a compact one-word label from a maturity pillar's full
// name (e.g. "Adoption & Behavioral Impact" → "Adoption"), skipping
// generic lead words so it works across org-specific taxonomies.
const PILLAR_STOPWORDS = new Set(['learning', 'and', 'the', 'of', 'a', 'an']);
export function pillarShortFromName(name, fallback) {
  if (!name) return fallback || '';
  const words = String(name).replace(/&/g, ' ').split(/\s+/).filter(Boolean);
  const w = words.find((x) => !PILLAR_STOPWORDS.has(x.toLowerCase()));
  return w || fallback || words[0] || '';
}

// The binding maturity constraint = lowest-scoring pillar (tie-break
// by pillar order so it matches the Maturity page's primary
// constraint). Returns the pillar_id or null.
export function computeConstraintPillarId(pillarScores) {
  if (!Array.isArray(pillarScores) || pillarScores.length === 0) return null;
  return [...pillarScores]
    .sort((a, b) => (a.score - b.score) || String(a.pillar_id).localeCompare(String(b.pillar_id)))[0]
    ?.pillar_id || null;
}
